import { useEffect, useState, useMemo, useRef } from "react";
import api, { fmtPKR, fmtDateTime, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Plus, X, Trash2, Printer, FileDown, Search } from "lucide-react";
import { toast } from "sonner";
import PageShell from "../components/PageShell";

export default function Returns() {
  const [returns, setReturns] = useState([]);
  const [products, setProducts] = useState([]);
  const [parties, setParties] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saleInvoice, setSaleInvoice] = useState("");
  const [linkedSale, setLinkedSale] = useState(null);
  const [customerType, setCustomerType] = useState("walkin");
  const [selectedParty, setSelectedParty] = useState("");
  const [walkinName, setWalkinName] = useState("Walk-in Customer");
  const [items, setItems] = useState([]);
  const [refundMethod, setRefundMethod] = useState("cash");
  const [notes, setNotes] = useState("");
  const [prodSearch, setProdSearch] = useState("");
  const [showDrop, setShowDrop] = useState(false);
  const dropRef = useRef(null);

  const load = async () => {
    const r = await api.get("/returns");
    setReturns(r.data);
  };
  const loadRefs = async () => {
    const [p, c] = await Promise.all([
      api.get("/products"),
      api.get("/parties", { params: { type: "customer" } }),
    ]);
    setProducts(p.data);
    setParties(c.data);
  };
  useEffect(() => {
    load();
    loadRefs();
  }, []);

  const total = useMemo(
    () => items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.rate) || 0), 0),
    [items]
  );

  const filtered = useMemo(() => {
    if (!prodSearch.trim()) return products;
    const q = prodSearch.trim().toLowerCase();
    return products.filter(
      (p) =>
        (p.name || "").toLowerCase().includes(q) ||
        (p.product_code || "").toLowerCase().includes(q) ||
        (p.brand || "").toLowerCase().includes(q)
    );
  }, [products, prodSearch]);

  const lookupInvoice = async () => {
    if (!saleInvoice.trim()) return;
    try {
      const r = await api.get("/sales", { params: { limit: 500 } });
      const s = r.data.find(
        (x) => x.invoice_no.toLowerCase() === saleInvoice.trim().toLowerCase()
      );
      if (!s) return toast.error("Invoice not found");
      setLinkedSale(s);
      setCustomerType(s.party_id ? "existing" : "walkin");
      if (s.party_id) setSelectedParty(s.party_id);
      else setWalkinName(s.customer_name);
      // Prefill items — look up each product to attach brand/barcode info
      setItems(
        s.items.map((it) => {
          const prod = products.find((p) => p.id === it.product_id);
          return {
            product_id: it.product_id,
            product_code: it.product_code,
            name: it.name,
            brand: prod?.brand || "",
            barcode: prod?.barcode || "",
            quantity: it.quantity,
            rate: it.rate,
            reason: "",
            is_manual: !it.product_id,
          };
        })
      );
      toast.success(`Loaded ${s.invoice_no}`);
    } catch (e) {
      toast.error("Lookup failed");
    }
  };

  const addProduct = (p) => {
    setItems((c) => [
      ...c,
      {
        product_id: p.id,
        product_code: p.product_code,
        name: p.name,
        brand: p.brand || "",
        barcode: p.barcode || "",
        quantity: 1,
        rate: p.sale_price,
        reason: "",
        is_manual: false,
      },
    ]);
    setProdSearch("");
    setShowDrop(false);
  };
  const addManual = () => {
    setItems((c) => [
      ...c,
      {
        product_id: null,
        product_code: "MANUAL",
        name: "",
        quantity: 1,
        rate: 0,
        reason: "",
        is_manual: true,
      },
    ]);
  };
  const upd = (i, k, v) => setItems((c) => c.map((it, ii) => (ii === i ? { ...it, [k]: v } : it)));
  const del = (i) => setItems((c) => c.filter((_, ii) => ii !== i));

  const submit = async () => {
    if (items.length === 0) return toast.error("Add items");
    if (items.some((it) => !it.name)) return toast.error("Every item needs a name");
    if (customerType === "existing" && !selectedParty) return toast.error("Select customer");
    if (refundMethod === "ledger" && customerType !== "existing")
      return toast.error("Ledger refund requires existing customer");
    try {
      const payload = {
        original_sale_id: linkedSale?.id || null,
        customer_type: customerType,
        party_id: customerType === "existing" ? selectedParty : null,
        customer_name:
          customerType === "existing"
            ? parties.find((p) => p.id === selectedParty)?.name || "Customer"
            : walkinName || "Walk-in Customer",
        items: items.map((it) => ({
          product_id: it.product_id,
          product_code: it.product_code,
          name: it.name,
          brand: it.brand || "",
          barcode: it.barcode || "",
          quantity: Number(it.quantity) || 0,
          rate: Number(it.rate) || 0,
          amount: (Number(it.quantity) || 0) * (Number(it.rate) || 0),
          reason: it.reason || "",
        })),
        refund_method: refundMethod,
        notes,
      };
      const r = await api.post("/returns", payload);
      toast.success(`Return ${r.data.return_no} saved`);
      // Auto open credit-note PDF
      openPdf(r.data.id);
      setDialogOpen(false);
      resetForm();
      load();
      loadRefs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const resetForm = () => {
    setSaleInvoice("");
    setLinkedSale(null);
    setCustomerType("walkin");
    setSelectedParty("");
    setWalkinName("Walk-in Customer");
    setItems([]);
    setRefundMethod("cash");
    setNotes("");
    setProdSearch("");
  };

  const openPdf = async (id) => {
    const token = localStorage.getItem("afridi_token");
    const res = await fetch(`${API}/returns/${id}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank");
    if (w) setTimeout(() => { try { w.focus(); w.print(); } catch (e) {} }, 800);
  };
  const downloadPdf = async (id, no) => {
    const token = localStorage.getItem("afridi_token");
    const res = await fetch(`${API}/returns/${id}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `credit_note_${no}.pdf`;
    a.click();
  };
  const delReturn = async (r) => {
    if (!window.confirm(`Delete return ${r.return_no}? Stock changes will be reversed.`)) return;
    await api.delete(`/returns/${r.id}`);
    toast.success("Deleted");
    load();
    loadRefs();
  };

  return (
    <PageShell
      ticker={[
        { label: "Console", value: "RETURNS", live: true },
        { label: "Total", value: String(returns.length).padStart(3, "0"), mono: true },
        { label: "Currency", value: "PKR ₨" },
      ]}
      eyebrow="Chapter · Returns & credit notes"
      title={<>Take it <em className="italic text-blue-700">back</em></>}
      description="Accept returned parts, restore stock, refund cash or credit the ledger — and print a proper credit note."
      colophon="Returns"
      cta={
        <Button
          onClick={() => setDialogOpen(true)}
          className="bg-slate-900 hover:bg-slate-800 text-white"
          data-testid="returns-new-btn"
        >
          <Plus size={16} className="mr-2" /> New Return
        </Button>
      }
    >
      <Card className="card-premium overflow-hidden" data-testid="returns-page">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.24em] text-slate-500 bg-slate-50/60">
              <th className="px-4 py-3 text-left font-medium">Return No.</th>
              <th className="px-4 py-3 text-left font-medium">Date</th>
              <th className="px-4 py-3 text-left font-medium">Customer</th>
              <th className="px-4 py-3 text-left font-medium">Original</th>
              <th className="px-4 py-3 text-left font-medium">Refund</th>
              <th className="px-4 py-3 text-right font-medium">Items</th>
              <th className="px-4 py-3 text-right font-medium">Total</th>
              <th className="px-4 py-3 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {returns.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50 align-top" data-testid={`return-row-${r.return_no}`}>
                <td className="px-4 py-2 font-mono-tabular text-xs">{r.return_no}</td>
                <td className="px-4 py-2 text-slate-500 text-xs">{fmtDateTime(r.date)}</td>
                <td className="px-4 py-2">
                  <div className="text-slate-900">{r.customer_name}</div>
                  {Array.isArray(r.items) && r.items.length > 0 && (
                    <div className="mt-1 text-[11px] text-slate-500 space-y-0.5">
                      {r.items.map((it, idx) => (
                        <div key={idx} className="font-mono-tabular leading-snug">
                          <span className="text-slate-400">{it.product_code || "-"}</span>
                          <span className="mx-1">·</span>
                          <span className="text-slate-700">{it.name}</span>
                          {it.brand ? (
                            <span className="text-slate-500"> · {it.brand}</span>
                          ) : null}
                          <span className="text-slate-500"> · qty {it.quantity}</span>
                          <span className="text-slate-500"> · {fmtPKR(it.rate)}</span>
                          {it.reason ? (
                            <span className="text-slate-400 italic"> · {it.reason}</span>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-4 py-2 text-xs font-mono-tabular text-slate-500">
                  {r.original_invoice_no || "-"}
                </td>
                <td className="px-4 py-2 text-xs uppercase tracking-wider text-slate-500">
                  {r.refund_method}
                </td>
                <td className="px-4 py-2 text-right tabular">{r.items?.length || 0}</td>
                <td className="px-4 py-2 text-right tabular font-semibold">{fmtPKR(r.total)}</td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => openPdf(r.id)} className="text-slate-500 hover:text-blue-700 mr-3" title="Print" data-testid={`return-print-${r.return_no}`}>
                    <Printer size={14} />
                  </button>
                  <button onClick={() => downloadPdf(r.id, r.return_no)} className="text-slate-500 hover:text-blue-700 mr-3" title="Download PDF" data-testid={`return-pdf-${r.return_no}`}>
                    <FileDown size={14} />
                  </button>
                  <button onClick={() => delReturn(r)} className="text-slate-500 hover:text-red-700" title="Delete return & reverse stock" data-testid={`return-delete-${r.return_no}`}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {returns.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-16 text-center">
                  <div className="font-serif italic text-slate-400">No returns yet</div>
                  <div className="text-xs text-slate-400 mt-2">Click "New Return" to record one.</div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>

      {/* New return dialog */}
      <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) resetForm(); }}>
        <DialogContent className="max-w-4xl" data-testid="return-dialog">
          <DialogHeader>
            <DialogTitle>New Return / Credit Note</DialogTitle>
          </DialogHeader>

          {/* Link invoice */}
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Label className="text-[10px] uppercase tracking-[0.24em] text-slate-500">
                Original Invoice (optional)
              </Label>
              <Input
                value={saleInvoice}
                onChange={(e) => setSaleInvoice(e.target.value)}
                placeholder="INV-0001"
                data-testid="return-invoice-input"
              />
            </div>
            <Button variant="outline" onClick={lookupInvoice} data-testid="return-lookup-btn">
              <Search size={14} className="mr-2" /> Lookup
            </Button>
          </div>
          {linkedSale && (
            <div className="text-xs text-slate-600 bg-blue-50 border border-blue-200 rounded px-3 py-2">
              Linked to <b>{linkedSale.invoice_no}</b> · {linkedSale.customer_name} ·{" "}
              {fmtPKR(linkedSale.grand_total)}
            </div>
          )}

          {/* Customer */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.24em] text-slate-500">
                Customer type
              </Label>
              <div className="flex gap-2 mt-1">
                <button
                  onClick={() => setCustomerType("walkin")}
                  className={`flex-1 px-3 py-2 text-xs rounded border ${customerType === "walkin" ? "bg-slate-900 text-white border-slate-900" : "border-slate-200"}`}
                >
                  Walk-in
                </button>
                <button
                  onClick={() => setCustomerType("existing")}
                  className={`flex-1 px-3 py-2 text-xs rounded border ${customerType === "existing" ? "bg-slate-900 text-white border-slate-900" : "border-slate-200"}`}
                >
                  Existing
                </button>
              </div>
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-[0.24em] text-slate-500">
                {customerType === "walkin" ? "Name" : "Party"}
              </Label>
              {customerType === "walkin" ? (
                <Input value={walkinName} onChange={(e) => setWalkinName(e.target.value)} data-testid="return-walkin-name" />
              ) : (
                <Select value={selectedParty} onValueChange={setSelectedParty}>
                  <SelectTrigger data-testid="return-party-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent>
                    {parties.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} {p.phone ? `· ${p.phone}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          {/* Add product */}
          <div className="relative">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                <Input
                  value={prodSearch}
                  onChange={(e) => { setProdSearch(e.target.value); setShowDrop(true); }}
                  onFocus={() => setShowDrop(true)}
                  onBlur={() => setTimeout(() => setShowDrop(false), 150)}
                  placeholder="Search inventory to add returned item…"
                  className="pl-9"
                  data-testid="return-prod-search"
                />
              </div>
              <Button variant="outline" onClick={addManual} data-testid="return-add-manual">
                <Plus size={14} className="mr-1" /> Manual line
              </Button>
            </div>
            {showDrop && filtered.length > 0 && (
              <div
                ref={dropRef}
                className="absolute left-0 right-0 mt-1 z-30 card-premium max-h-64 overflow-y-auto"
                style={{ boxShadow: "0 8px 24px -6px rgba(15,23,42,0.15)" }}
              >
                {filtered.map((p) => (
                  <button
                    key={p.id}
                    onMouseDown={() => addProduct(p)}
                    className="w-full text-left px-4 py-2 hover:bg-blue-50 text-sm border-b border-slate-100 flex items-center gap-3"
                  >
                    <span className="font-mono-tabular text-[10px] text-slate-500 w-16 shrink-0">{p.product_code}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-slate-900 truncate">{p.name}</div>
                      <div className="text-[10px] text-slate-500 truncate">
                        {p.brand || "—"}
                        {p.barcode ? ` · ${p.barcode}` : ""}
                        {" · "}Stock {p.current_stock}
                        {p.sets ? ` · ${p.sets} sets` : ""}
                        {p.pieces ? ` · ${p.pieces} pcs` : ""}
                      </div>
                    </div>
                    <span className="tabular text-slate-600 shrink-0">{fmtPKR(p.sale_price)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Items */}
          <div className="border border-slate-200 rounded overflow-hidden max-h-64 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-slate-400 text-sm">No items yet</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-[10px] uppercase tracking-[0.24em] text-slate-500">
                    <th className="px-3 py-2 text-left">Item</th>
                    <th className="px-3 py-2 text-right w-20">Qty</th>
                    <th className="px-3 py-2 text-right w-28">Rate</th>
                    <th className="px-3 py-2 text-left w-32">Reason</th>
                    <th className="px-3 py-2 text-right w-28">Amount</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={i} className="border-t border-slate-100" data-testid={`return-item-${i}`}>
                      <td className="px-3 py-2">
                        {it.is_manual ? (
                          <Input value={it.name} onChange={(e) => upd(i, "name", e.target.value)} placeholder="Item name" className="h-8 text-sm" data-testid={`return-name-${i}`} />
                        ) : (
                          <>
                            <div className="text-slate-900">{it.name}</div>
                            <div className="text-[10px] text-slate-500 font-mono-tabular">
                              {it.product_code}
                              {it.brand ? ` · ${it.brand}` : ""}
                              {it.barcode ? ` · ${it.barcode}` : ""}
                            </div>
                          </>
                        )}
                      </td>
                      <td className="px-3 py-2"><Input type="number" min="0" value={it.quantity} onChange={(e) => upd(i, "quantity", e.target.value)} className="h-8 text-right tabular" data-testid={`return-qty-${i}`} /></td>
                      <td className="px-3 py-2"><Input type="number" min="0" value={it.rate} onChange={(e) => upd(i, "rate", e.target.value)} className="h-8 text-right tabular" data-testid={`return-rate-${i}`} /></td>
                      <td className="px-3 py-2"><Input value={it.reason} onChange={(e) => upd(i, "reason", e.target.value)} placeholder="e.g. defective" className="h-8 text-sm" /></td>
                      <td className="px-3 py-2 text-right tabular font-medium">{fmtPKR((Number(it.quantity) || 0) * (Number(it.rate) || 0))}</td>
                      <td className="px-3 py-2 text-right"><button onClick={() => del(i)} className="text-slate-400 hover:text-red-600"><X size={14} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Refund + Notes + Total */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.24em] text-slate-500">Refund method</Label>
              <div className="flex gap-2 mt-1">
                <button onClick={() => setRefundMethod("cash")} className={`flex-1 px-3 py-2 text-xs rounded border ${refundMethod === "cash" ? "bg-emerald-600 text-white border-emerald-600" : "border-slate-200"}`} data-testid="return-refund-cash">Cash</button>
                <button onClick={() => setRefundMethod("ledger")} className={`flex-1 px-3 py-2 text-xs rounded border ${refundMethod === "ledger" ? "bg-blue-700 text-white border-blue-700" : "border-slate-200"}`} data-testid="return-refund-ledger">Ledger credit</button>
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.24em] text-slate-500">Notes</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" placeholder="Optional" />
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500">Total refund</div>
              <div className="big-number text-2xl mt-1" data-testid="return-total">{fmtPKR(total)}</div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={submit} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="return-submit-btn">
              <Printer size={14} className="mr-2" /> Save &amp; Print Credit Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
